import { useCallback, useEffect, useState } from "react";
import { DeleteOutlined, EditOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Dropdown, Form, Input, Menu, Popconfirm, Row, Select, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import { TNA_BY_STYLE_BASE, TNA_SALES_CONTRACT, TNA_STYLES } from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import ListTable from "common/ListTable";
import { hasPermission } from "utils/functions";
import {
  TNA_BY_STYLE_CREATE_PERMISSION,
  TNA_BY_STYLE_DELETE_PERMISSION,
  TNA_BY_STYLE_EDIT_PERMISSION,
} from "routes/permissions";

const TNAByStyles = () => {
  const navigate = useNavigate();
  const { Title } = Typography;
  const { Option } = Select;

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [salesContracts, setSalesContracts] = useState([]);
  const [styles, setStyles] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const [filterForm] = Form.useForm();

  const getSalesContracts = async (reference_no) => {
    const response = await getData(TNA_SALES_CONTRACT, false, {
      per_page: 100,
      ...(reference_no ? { reference_no } : {}),
    });

    if (response?.data?.code === 200) {
      setSalesContracts(response?.data?.data?.data || []);
    }
  };

  const getStyles = async (salesContractId, style_no) => {
    if (!salesContractId) {
      setStyles([]);
      return;
    }

    const response = await getData(`${TNA_STYLES}/${salesContractId}`, false, {
      ...(style_no ? { style_no } : {}),
    });

    if (response?.data?.code === 200) {
      setStyles(response?.data?.data || []);
    }
  };

  const getAllTNAByStyles = useCallback(async () => {
    const values = filterForm.getFieldsValue();

    setLoading(true);
    setFetchError(null);
    const response = await getData(TNA_BY_STYLE_BASE, false, {
      per_page: 1000,
      ...(values?.sales_contract_id ? { sales_contract_id: values?.sales_contract_id } : {}),
      ...(values?.style_id ? { style_id: values?.style_id } : {}),
    });
    setLoading(false);

    if (response?.data?.code === 200) {
      setTableData(response?.data?.data?.data || []);
      return;
    }

    setTableData([]);
    setFetchError("Could not load this list.");
  }, [filterForm]);

  useEffect(() => {
    getSalesContracts();
  }, []);

  useEffect(() => {
    getAllTNAByStyles();
  }, [getAllTNAByStyles, refresh]);

  const handleDelete = async (id) => {
    const response = await deleteData(`${TNA_BY_STYLE_BASE}/${id}`);
    if (response) {
      HandleNotification("success", "bottomRight", "TNA By Style deleted successfully", null);
      getAllTNAByStyles();
      return;
    }

    HandleNotification("error", "bottomRight", "Something went wrong", null);
  };

  const actionMenu = (record) => (
    <Menu
      items={[
        {
          key: "edit",
          label: (
            <Link to={`/tanda/update-tna-by-style/${record?.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          disabled: !hasPermission([TNA_BY_STYLE_EDIT_PERMISSION]),
        },
        {
          key: "delete",
          label: (
            <Popconfirm title="Delete this TNA By Style?" onConfirm={() => handleDelete(record?.id)}>
              <span style={{ color: "red" }}>
                <DeleteOutlined /> Delete
              </span>
            </Popconfirm>
          ),
          disabled: !hasPermission([TNA_BY_STYLE_DELETE_PERMISSION]),
        },
      ]}
    />
  );

  const columns = [
    {
      title: "Style(s)",
      key: "styles",
      render: (_, record) =>
        record?.styles?.length ? record.styles.map((style) => style?.style_no).join(", ") : record?.style?.style_no,
    },
    { title: "Sales Contract No", dataIndex: ["sales_contract", "reference_no"], key: "sc_no" },
    { title: "Buyer", dataIndex: ["buyer", "name"], key: "buyer" },
    { title: "Buying Office", dataIndex: ["buying_office", "name"], key: "buying_office" },
    {
      title: "PCD",
      dataIndex: ["pcd_date"],
      key: "pcd_date",
      render: (pcdDate, record) => {
        const isPlaceholder = !pcdDate || String(pcdDate).startsWith("1970");
        return isPlaceholder ? record?.sales_contract?.pcd_date || "" : pcdDate;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Dropdown overlay={actionMenu(record)}>
          <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const onReset = () => {
    filterForm.resetFields();
    setStyles([]);
    setRefresh((p) => p + 1);
  };

  return (
    <AppContent
      header={
        <AppPageHeader
          title={<Title level={5}>TNA By Styles</Title>}
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              handleAddItemOpen={() => navigate("/tanda/add-tna-by-style")}
              disabled={!hasPermission([TNA_BY_STYLE_CREATE_PERMISSION])}
              addButtonText={hasPermission([TNA_BY_STYLE_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
          }
        />
      }
      content={
        <>
          {showFilter && (
            <Card style={{ marginBottom: 12 }}>
              <Form form={filterForm} layout="vertical" onFinish={getAllTNAByStyles}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name="sales_contract_id" label="Sales Contract No">
                      <Select
                        size="small"
                        showSearch
                        allowClear
                        placeholder="Select sales contract"
                        optionFilterProp="children"
                        onSearch={getSalesContracts}
                        onSelect={(value) => {
                          filterForm.setFieldsValue({ style_id: undefined });
                          getStyles(value);
                        }}
                      >
                        {salesContracts?.map((item) => (
                          <Option key={item?.id} value={item?.id}>
                            {item?.reference_no}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="style_id" label="Style">
                      <Select
                        size="small"
                        showSearch
                        allowClear
                        placeholder="Select style"
                        optionFilterProp="children"
                        onSearch={(value) => getStyles(filterForm.getFieldValue("sales_contract_id"), value)}
                      >
                        {styles?.map((item) => (
                          <Option key={item?.id} value={item?.id}>
                            {item?.style_no}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col>
                    <Button type="primary" size="small" htmlType="submit" icon={<SearchOutlined />}>
                      Search
                    </Button>
                  </Col>
                  <Col>
                    <Button size="small" onClick={onReset}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card>
          )}

          <ListTable
            rowKey="id"
            loading={loading}
            error={fetchError}
            onRetry={() => getAllTNAByStyles()}
            emptyText="No records to show yet."
            dataSource={tableData}
            columns={columns}
            pagination={false}
          />
        </>
      }
    />
  );
};

export default TNAByStyles;
